import { QueryClient } from '@tanstack/react-query';
import {
  type OpeningDetailResponse,
  type SourcingOpeningUpdateParameters,
} from 'api-clients/monolith';

import { queryKeys } from '@/query-keys';

type JobDetailsData = { openings?: OpeningDetailResponse[] };

// The fields a save writes to the opening's Location, which its sibling
// openings share. Hire stores a blank one as null.
const LOCATION_KEYS = [
  'state_code',
  'street_address',
  'city',
  'postal_code',
] as const;

/**
 * The opening as the job-details query last returned it. Earlier selections
 * stay cached, so the mounted query wins, then the newest.
 */
export function findCachedOpening(
  queryClient: QueryClient,
  openingExternalId: string,
): OpeningDetailResponse | undefined {
  return queryClient
    .getQueryCache()
    .findAll({ queryKey: queryKeys.jobPosting.jobDetails })
    .sort(
      (a, b) =>
        Number(b.isActive()) - Number(a.isActive()) ||
        b.state.dataUpdatedAt - a.state.dataUpdatedAt,
    )
    .flatMap(query => (query.state.data as JobDetailsData)?.openings ?? [])
    .find(opening => opening.external_id === openingExternalId);
}

/**
 * Records a confirmed save in every cached job-details query, so the next
 * save and the next refetch compare against what Hire now holds. The address
 * and state go to every opening on the same Location.
 */
export function writeSavedOpening(
  queryClient: QueryClient,
  openingExternalId: string,
  requestBody: SourcingOpeningUpdateParameters,
): void {
  const locationExternalId = findCachedOpening(
    queryClient,
    openingExternalId,
  )?.location_external_id;
  const location: Partial<
    Pick<OpeningDetailResponse, (typeof LOCATION_KEYS)[number]>
  > = {};
  for (const key of LOCATION_KEYS) {
    const value = requestBody[key];
    if (value !== undefined) location[key] = value.trim() || null;
  }

  queryClient.setQueriesData<JobDetailsData>(
    { queryKey: queryKeys.jobPosting.jobDetails },
    data => {
      if (!data?.openings) return data;
      return {
        ...data,
        openings: data.openings.map(opening => {
          if (opening.external_id === openingExternalId) {
            return {
              ...opening,
              ...location,
              job_description:
                requestBody.description ?? opening.job_description,
            };
          }
          if (
            locationExternalId !== undefined &&
            opening.location_external_id === locationExternalId
          ) {
            return { ...opening, ...location };
          }
          return opening;
        }),
      };
    },
  );
}
